import { AlertTriangleIcon, CheckIcon, XIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { ShipmentStatusTag } from '@/components';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import type { AnimalShipmentConflict } from '@/services/animal-management';

type AnimalShipmentOverrideDialogProps = {
  conflicts: AnimalShipmentConflict[] | null;
  isSubmitting: boolean;
  onConfirm: () => void;
  onCancel: () => void;
};

export function AnimalShipmentOverrideDialog({
  conflicts,
  isSubmitting,
  onConfirm,
  onCancel,
}: AnimalShipmentOverrideDialogProps) {
  const { t } = useTranslation();

  return (
    <Dialog
      open={conflicts !== null}
      onOpenChange={(open) => {
        if (!open) onCancel();
      }}
    >
      <DialogContent className="md:max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-amber-700">
            <AlertTriangleIcon className="size-5" />
            {t(
              'animalManagement.shipping.overrideTitle',
              'Animals already on a shipment',
            )}
          </DialogTitle>
          <DialogDescription>
            {t('animalManagement.shipping.overrideDescription', {
              defaultValue:
                '{count} animals are already on another shipment. Move them to this shipment?',
              count: conflicts?.length ?? 0,
            })}
          </DialogDescription>
        </DialogHeader>

        <DialogBody>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>
                  {t('animalManagement.columns.nameTag', 'Name/Tag')}
                </TableHead>
                <TableHead>
                  {t('animalManagement.columns.animalType', 'Animal Type')}
                </TableHead>
                <TableHead>
                  {t(
                    'animalManagement.shipping.currentShipment',
                    'Current Shipment',
                  )}
                </TableHead>
                <TableHead>
                  {t('shipping.shipmentStatus', 'Shipment Status')}
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {conflicts?.map((conflict) => (
                <TableRow key={conflict.animalId}>
                  <TableCell>{conflict.animalName}</TableCell>
                  <TableCell>{conflict.animalType}</TableCell>
                  <TableCell>{conflict.shipmentNumber}</TableCell>
                  <TableCell>
                    <ShipmentStatusTag value={conflict.shipmentStatus} />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </DialogBody>

        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            disabled={isSubmitting}
            onClick={onCancel}
          >
            <XIcon className="mr-2 size-4" />
            {t('common.cancel', 'Cancel')}
          </Button>
          <Button
            type="button"
            className="bg-amber-600 hover:bg-amber-700"
            disabled={isSubmitting}
            onClick={onConfirm}
          >
            <CheckIcon className="mr-2 size-4" />
            {t('animalManagement.shipping.overrideConfirm', 'Move Animals')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
